import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { api, type UsageReport } from "../api";
import { Link } from "react-router";
import { Empty, ErrorMessage, Loading } from "../components";
import { ForecastCard } from "../ForecastCard";
import { ActivityHeatmap, CacheCard, ModelMixChart, SavingsHero, TopSessions } from "../HomeCards";
import { NoActivity } from "../NoActivity";
import { count, money, pct, tokens } from "../format";
import { useForgeEvent } from "../hooks";

export function RangeToggle({ value, onChange, options = [7, 30, 90] }: { value: number; onChange: (d: number) => void; options?: number[] }) {
  const ref = useRef<HTMLElement>(null);
  useForgeEvent(ref, "forge-button-toggle-group-change", (e: CustomEvent) => onChange(Number(e.detail)));
  return (
    <forge-button-toggle-group ref={ref} value={String(value)} mandatory dense aria-label="Date range">
      {options.map((d) => <forge-button-toggle key={d} value={String(d)}>{d === 1 ? "24 hours" : `${d} days`}</forge-button-toggle>)}
    </forge-button-toggle-group>
  );
}

export function UsagePage() {
  const [days, setDays] = useState(30);
  const usage = useQuery({ queryKey: ["usage", days], queryFn: () => api.usage(days) });
  const keys = useQuery({ queryKey: ["keys"], queryFn: api.keys });

  // Home for someone with no keys yet: lead with getting started, not an empty forecast.
  if (keys.data && keys.data.length === 0) {
    return (
      <forge-card>
        <Empty icon="vpn_key" title="Welcome. Start with a key."
          message="Create a key for each machine or tool, then set the tool up to use it. Your spend and a month-end forecast appear here once it is in use."
          action={<div className="row gap-small">
            <Link to="/keys?create=1"><forge-button variant="raised">Create a key</forge-button></Link>
            <Link to="/setup"><forge-button variant="outlined">See how to set up a tool</forge-button></Link>
          </div>} />
      </forge-card>
    );
  }

  return (
    <>
      <div className="row spread wrap gap-small">
        <p className="forge-typography--body2 muted grow">
          Spend is LiteLLM's estimate at list prices, not an invoice. Daily totals can lag by a minute or so.
        </p>
        <RangeToggle value={days} onChange={setDays} />
      </div>
      {usage.isPending ? <Loading /> : usage.error ? <ErrorMessage error={usage.error} /> : usage.data.totals.requests === 0 ? (
        <>
          <ForecastCard />
          <forge-card><NoActivity quiet={{ title: "No usage in this period", message: "Try a longer range; requests made with your keys show up here." }} /></forge-card>
        </>
      ) : <UsageBody r={usage.data} days={days} />}
    </>
  );
}

function UsageBody({ r, days }: { r: UsageReport; days: number }) {
  const t = r.totals;
  return (
    <>
      <SavingsHero r={r} days={days} onSeeSessions={() => document.getElementById("top-sessions")?.scrollIntoView({ behavior: "smooth" })} />
      <div className="grid-2">
        <ForecastCard compact />
        <CacheCard r={r} />
      </div>
      <ModelMixChart r={r} />
      <TopSessions />
      <ActivityHeatmap />
      <div className="stack gap-large">
        <forge-card>
          <h2 className="forge-typography--heading2 card-title">By model</h2>
          <MetricTable rows={r.byModel.map((m) => ({ label: m.model, ...m }))} total={t.spend} />
        </forge-card>
        <forge-card>
          <h2 className="forge-typography--heading2 card-title">By key</h2>
          <MetricTable rows={r.byKey.map((k) => ({ label: (k.alias ?? `…${k.keyId.slice(-8)}`).replace(/^[^:]*:/, ""), ...k }))} total={t.spend} />
        </forge-card>
      </div>
    </>
  );
}

function MetricTable({ rows, total }: { rows: Array<{ label: string; spend: number; requests: number; promptTokens: number; completionTokens: number; cacheReadTokens: number; cacheSavings: number }>; total: number }) {
  return (
    <div className="table-wrap">
      <table className="forge-data-table">
        <thead><tr><th>Name</th><th className="num">Requests</th><th className="num">Input</th><th className="num">Cache read</th><th className="num">Output</th><th className="num">Cache saved</th><th className="num">Spend (est.)</th><th>Share</th></tr></thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.label}>
              <td>{m.label}</td>
              <td className="num">{count(m.requests)}</td>
              <td className="num">{tokens(m.promptTokens)}</td>
              <td className="num">{tokens(m.cacheReadTokens)}</td>
              <td className="num">{tokens(m.completionTokens)}</td>
              <td className="num">{m.cacheSavings > 0 ? money(m.cacheSavings) : "–"}</td>
              <td className="num">{money(m.spend)}</td>
              <td style={{ minWidth: "80px" }}><div className="bar-track"><div className="bar" style={{ width: total ? `${(m.spend / total) * 100}%` : 0 }} /></div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
